/**
 * Costs.jsx
 *
 * Per-case cost tracking panel. Visible only to roles with canSeeCosts
 * (Supervisor / SupervisorView / SuperAdmin). Editing requires canEditCosts
 * (Supervisor / SuperAdmin).
 *
 * Props:
 *   caseId       — SQL case id (integer)
 *   country      — the case's country (defaults a new cost's currency: US→USD, etc.)
 *   onChange()   — optional callback fired after add/edit/delete succeeds
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCosts, postCost, patchCost, deleteCost } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useAppConfig } from '../contexts/AppConfigContext.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import SearchableSelect from './SearchableSelect.jsx';
import DateInput from './DateInput.jsx';
import { CostCategoryPill } from './CostChip.jsx';
import { defaultCurrencyForCountry } from '@shared/currency.js';

// ── Formatting helpers ───────────────────────────────────────────────────────

function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
  } catch {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// ── Cost modal (add + edit) ─────────────────────────────────────────────────

function CostModal({ title, initial, categories, currencies, country, onClose, onSave, saving, error }) {
  // New cost: prefer the case country's currency when it's a configured option;
  // otherwise the config default. Editing keeps the row's own currency.
  const suggested = defaultCurrencyForCountry(country);
  const suggestedAvailable = currencies.some(c => c.code === suggested && !c.archived);
  const configDefault = currencies.find(c => c.isDefault)?.code || currencies[0]?.code || 'GBP';
  const [amount,      setAmount]      = useState(initial?.amount      ?? '');
  const [currency,    setCurrency]    = useState(initial?.currency    ?? (suggestedAvailable ? suggested : configDefault));
  const [categoryKey, setCategoryKey] = useState(initial?.categoryKey ?? (categories[0]?.key || ''));
  const [incurredAt,  setIncurredAt]  = useState(initial?.incurredAt  ?? todayIso());
  const [notes,       setNotes]       = useState(initial?.notes       ?? '');

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [saving, onClose]);

  const numericAmount = Number(amount);
  // Amount must be a positive sum — a £0 cost line is never valid.
  const canSubmit = Number.isFinite(numericAmount) && numericAmount > 0
                    && !!currency && !!categoryKey && /^\d{4}-\d{2}-\d{2}$/.test(incurredAt);

  function submit() {
    if (!canSubmit || saving) return;
    onSave({
      amount:      numericAmount,
      currency,
      categoryKey,
      incurredAt,
      notes:       notes.trim() || null,
    });
  }

  return (
    <div
      onClick={saving ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '60px 24px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          width: '100%', maxWidth: 520,
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px 16px',
          borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{title}</div>
          <button onClick={onClose} disabled={saving} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-3)', fontSize: 20, lineHeight: 1, padding: '0 4px',
          }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
            <label style={{ display: 'block' }}>
              <div className="label-sm">Amount</div>
              <input
                className="input"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                disabled={saving}
                autoFocus
              />
            </label>
            <div>
              <div className="label-sm">Currency</div>
              <SearchableSelect
                name="cost-currency"
                value={currency}
                onChange={e => setCurrency(e.target.value)}
                disabled={saving}
                options={currencies
                  .filter(c => !c.archived || c.code === currency)
                  .map(c => ({ value: c.code, label: `${c.symbol} ${c.code}` }))}
              />
            </div>
          </div>

          <div>
            <div className="label-sm">Category</div>
            <SearchableSelect
              name="cost-category"
              value={categoryKey}
              onChange={e => setCategoryKey(e.target.value)}
              disabled={saving}
              options={categories
                .filter(c => !c.archived || c.key === categoryKey)
                .map(c => ({ value: c.key, label: c.label }))}
            />
          </div>

          <label style={{ display: 'block' }}>
            <div className="label-sm">Date incurred</div>
            <DateInput
              value={incurredAt}
              onChange={e => setIncurredAt(e.target.value)}
              disabled={saving}
            />
          </label>

          <label style={{ display: 'block' }}>
            <div className="label-sm">Notes <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span></div>
            <textarea
              className="textarea"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              disabled={saving}
              rows={3}
              placeholder="e.g. settlement breakdown, invoice reference…"
            />
          </label>

          {error && (
            <div className="callout danger" style={{ marginTop: 0 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0 }}>
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <div>{error}</div>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '14px 24px',
          borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)',
          borderRadius: '0 0 12px 12px',
        }}>
          <button className="btn" onClick={onClose} disabled={saving} style={{ justifyContent: 'center', minWidth: 90 }}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={!canSubmit || saving} style={{ justifyContent: 'center', minWidth: 120 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────────

export default function Costs({ caseId, country, onChange }) {
  const { user } = useAuth();
  const { config } = useAppConfig();
  const canEdit = !!user?.canEditCosts;

  // Sorted by the admin-panel `order` field so reordering there takes effect here.
  const categories = (config.costCategories || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const currencies = config.currencies     || [];

  const [costs, setCosts]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);  // existing cost row, or null for "add"
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState(null);

  const [confirmDel, setConfirmDel] = useState(null);  // cost row queued for delete
  const [deleting, setDeleting]     = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    try {
      setLoading(true);
      setLoadError(null);
      const data = await getCosts(caseId);
      setCosts(Array.isArray(data) ? data : []);
    } catch (err) {
      setLoadError(err.message || 'Failed to load costs');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  // Totals per currency present on this case
  const totals = useMemo(() => {
    const out = {};
    for (const c of costs) {
      const cur = c.currency || 'GBP';
      out[cur] = (out[cur] || 0) + (Number(c.amount) || 0);
    }
    return out;
  }, [costs]);

  // Resolve category meta — falls back to the stored key if config no longer has it
  const categoryByKey = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.key] = c;
    return m;
  }, [categories]);

  function openAdd() {
    setEditing(null);
    setSaveError(null);
    setModalOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setSaveError(null);
    setModalOpen(true);
  }

  async function handleSave(payload) {
    setSaving(true);
    setSaveError(null);
    try {
      if (editing) {
        const updated = await patchCost(editing.id, payload);
        setCosts(prev => prev.map(c => c.id === updated.id ? updated : c));
      } else {
        const fresh = await postCost(caseId, payload);
        setCosts(prev => [fresh, ...prev]);
      }
      setModalOpen(false);
      onChange?.();
    } catch (err) {
      setSaveError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await deleteCost(confirmDel.id);
      setCosts(prev => prev.filter(c => c.id !== confirmDel.id));
      setConfirmDel(null);
      onChange?.();
    } catch (err) {
      setSaveError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="panel">
      <div className="panel-head" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <div className="panel-title">Costs</div>
          {!loading && Object.keys(totals).length > 0 && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, color: 'var(--text-2)', fontSize: 13 }}>
              <span>Total:</span>
              {Object.entries(totals).map(([cur, total], i) => (
                <React.Fragment key={cur}>
                  {i > 0 && <span style={{ color: 'var(--text-3)' }}>·</span>}
                  <strong style={{ color: 'var(--text)' }}>{formatMoney(total, cur)}</strong>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        {canEdit && (
          <button className="btn primary sm" onClick={openAdd}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14 }}>
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Add cost
          </button>
        )}
      </div>

      <div className="panel-body">
        {loading ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>Loading costs…</p>
        ) : loadError ? (
          <div className="callout danger" style={{ margin: 0 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <div>Could not load costs — {loadError}</div>
          </div>
        ) : costs.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            No costs recorded.
            {canEdit && <>{' '}<button className="btn ghost sm" style={{ display: 'inline', padding: '0 4px' }} onClick={openAdd}>Add the first one</button></>}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="costs-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--text-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  <th style={{ padding: '8px 8px', borderBottom: '1px solid var(--border)' }}>Date</th>
                  <th style={{ padding: '8px 8px', borderBottom: '1px solid var(--border)' }}>Category</th>
                  <th style={{ padding: '8px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>Amount</th>
                  <th style={{ padding: '8px 8px', borderBottom: '1px solid var(--border)' }}>Notes</th>
                  <th style={{ padding: '8px 8px', borderBottom: '1px solid var(--border)' }}>Added by</th>
                  {canEdit && <th style={{ padding: '8px 8px', borderBottom: '1px solid var(--border)', width: 70 }} />}
                </tr>
              </thead>
              <tbody>
                {costs.map(c => {
                  const cat = categoryByKey[c.categoryKey];
                  return (
                    <tr key={c.id}>
                      <td style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                        {formatDate(c.incurredAt)}
                      </td>
                      <td style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)' }}>
                        <CostCategoryPill cssClass={cat?.cssClass} label={cat?.label || c.categoryKey} />
                      </td>
                      <td style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {formatMoney(c.amount, c.currency)}
                      </td>
                      <td style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-2)', maxWidth: 320 }}>
                        {c.notes
                          ? <span title={c.notes}>{c.notes.length > 80 ? c.notes.slice(0, 80) + '…' : c.notes}</span>
                          : <span className="muted">—</span>}
                      </td>
                      <td style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                        {c.createdBy || '—'}
                      </td>
                      {canEdit && (
                        <td style={{ padding: '10px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button className="btn ghost sm" onClick={() => openEdit(c)} title="Edit">Edit</button>
                          <button
                            className="btn ghost sm"
                            onClick={() => setConfirmDel(c)}
                            title="Delete"
                            style={{ color: 'var(--danger)' }}
                          >
                            Delete
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <CostModal
          title={editing ? 'Edit cost' : 'Add cost'}
          initial={editing}
          categories={categories}
          currencies={currencies}
          country={country}
          onClose={() => !saving && setModalOpen(false)}
          onSave={handleSave}
          saving={saving}
          error={saveError}
        />
      )}

      {confirmDel && (
        <ConfirmDialog
          message={`Delete this ${formatMoney(confirmDel.amount, confirmDel.currency)} cost?`}
          detail="This removes it from totals and the case card. The deletion is recorded in case history."
          confirmLabel="Delete"
          danger
          saving={deleting}
          onCancel={() => !deleting && setConfirmDel(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
